import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { getFriends } from '../../utils/friendsService';
import { useCreateNotification } from '../../hooks/useCreateNotification';
import { UserListAvatar } from '../common/UserListAvatar';
import SingleEvent from '../../classes/events/SingleEvent';
import { toast } from 'react-toastify';
import Loading from '../layout/Loading';
import './ShareEventPopup.css';

function ShareEventPopup({ eventId, eventTitle, onClose }) {
  const { t } = useTranslation(['events', 'shared']);
  const createNotification = useCreateNotification({ cooldownMinutes: 1 });
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' or 'link'
  const [eventData, setEventData] = useState(null);

  const loadEventData = useCallback(async () => {
    try {
      const eventResult = await SingleEvent.loadEvent(eventId);
      if (eventResult.data) {
        setEventData(eventResult.data);
        return eventResult.data;
      }
    } catch (error) {
      console.error('Error loading event data:', error);
    }
    return null;
  }, [eventId]);

  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Upewnij się, że eventData jest załadowane
      let currentEventData = eventData;
      if (!currentEventData) {
        currentEventData = await loadEventData();
      }

      const result = await getFriends(user.id);
      if (result.success && result.data) {
        // Sprawdź status każdego znajomego
        const friendsWithStatus = await Promise.all(
          result.data.map(async (friend) => {
            let status = null;
            let canInvite = true;

            // Sprawdź czy znajomy jest hostem
            if (currentEventData && currentEventData.host_id === friend.id) {
              status = 'host';
              canInvite = false;
            } else {
              // Sprawdź czy znajomy jest już uczestnikiem
              const isParticipant = await SingleEvent.isParticipantInDB(eventId, friend.id);
              if (isParticipant) {
                status = 'participant';
                canInvite = false;
              } else {
                // Sprawdź czy znajomy ma już zaproszenie pending (tylko te, które current user wysłał)
                try {
                  const { data: existingInvitation } = await supabase
                    .from('event_invitations')
                    .select('id')
                    .eq('event_id', eventId)
                    .eq('inviter_id', user.id) // Tylko zaproszenia wysłane przez current user
                    .eq('invitee_id', friend.id)
                    .eq('status', 'pending')
                    .maybeSingle();
                  
                  if (existingInvitation) {
                    status = 'invited';
                    canInvite = false;
                  }
                } catch (invitationError) {
                  // Ignoruj błędy przy sprawdzaniu zaproszeń - nie blokuj ładowania listy
                  console.warn('Error checking invitation status for friend:', friend.id, invitationError);
                }
              }
            }

            return {
              ...friend,
              status,
              canInvite
            };
          })
        );

        setFriends(friendsWithStatus);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      toast.error(t('events.share.errorLoadingFriends'));
    } finally {
      setLoading(false);
    }
  }, [eventId, eventData, loadEventData, t]);

  // Load friends when component mounts or when activeTab changes to 'friends'
  useEffect(() => {
    if (activeTab === 'friends') {
      loadFriends();
    }
  }, [activeTab, loadFriends]);

  const handleFriendToggle = (friendId) => {
    const friend = friends.find(f => f.id === friendId);
    if (!friend || !friend.canInvite) {
      return; // Nie można wybrać znajomych, którzy nie mogą otrzymać zaproszenia
    }

    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  const handleSelectAll = () => {
    const availableFriends = friends.filter(f => f.canInvite);
    if (selectedFriends.length === availableFriends.length) {
      setSelectedFriends([]);
    } else {
      setSelectedFriends(availableFriends.map(f => f.id));
    }
  };

  const handleSendInvitations = async () => {
    // Sprawdź czy wydarzenie jest zakończone lub anulowane
    if (eventData) {
      if (eventData.is_cancelled === true) {
        toast.error(t('events.share.eventCancelled'));
        return;
      }
      
      if (eventData.status === 'ended') {
        toast.error(t('events.share.eventEnded'));
        return;
      }
    }
    
    // Filtruj tylko dostępnych znajomych
    const validSelectedFriends = selectedFriends.filter(friendId => {
      const friend = friends.find(f => f.id === friendId);
      return friend && friend.canInvite;
    });

    if (validSelectedFriends.length === 0) {
      toast.info(t('events.share.selectFriends'));
      return;
    }

    try {
      setSending(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t('shared.common.notLoggedIn'));
        return;
      }

      // Pobierz dane użytkownika dla powiadomienia
      const { data: userProfile } = await supabase
        .from('users')
        .select('nickname, name, first_name, last_name')
        .eq('id', user.id)
        .single();

      const userName = userProfile?.nickname || 
                       userProfile?.name || 
                       `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
                       t('events.share.user');

      // Utwórz zaproszenia w bazie danych i wyślij powiadomienia do wybranych znajomych
      const promises = validSelectedFriends.map(async (friendId) => {
        try {
          // Walidacja przed wysłaniem
          if (!friendId || typeof friendId !== 'string') {
            console.error('Invalid friendId:', friendId);
            return { success: false, error: new Error('Invalid friend ID') };
          }
          if (!eventId || typeof eventId !== 'string') {
            console.error('Invalid eventId:', eventId);
            return { success: false, error: new Error('Invalid event ID') };
          }

          // Utwórz zaproszenie w bazie danych
          const { error: invitationError } = await supabase
            .from('event_invitations')
            .insert({
              event_id: eventId,
              inviter_id: user.id,
              invitee_id: friendId,
              status: 'pending'
            })
            .select()
            .single();

          // Jeśli zaproszenie już istnieje (unique constraint), to nie jest błąd
          if (invitationError && invitationError.code !== '23505') {
            console.error('Error creating invitation:', invitationError);
            return { success: false, error: invitationError };
          }

          // Wyślij powiadomienie
          const result = await createNotification({
            userId: friendId,
            type: 'event_invitation',
            data: {
              event_title: eventTitle || 'Wydarzenie',
              inviter_name: userName || 'Użytkownik'
            },
            eventId: eventId,
            applicationId: null
          });
          return result;
        } catch (error) {
          console.error('Error creating invitation/notification for friend:', friendId, error);
          console.error('Error details:', {
            message: error?.message,
            code: error?.code,
            details: error?.details
          });
          return { success: false, error };
        }
      });

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const errorCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(t('events.share.invitationsSent', { count: successCount }));
        setSelectedFriends([]);
        // Odśwież listę znajomych aby zaktualizować statusy
        await loadFriends();
      }
      if (errorCount > 0) {
        toast.warning(t('events.share.someInvitationsFailed', { count: errorCount }));
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(t('events.share.errorSending'));
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async () => {
    // Sprawdź czy wydarzenie jest zakończone lub anulowane
    if (eventData) {
      if (eventData.is_cancelled === true) {
        toast.error(t('events.share.eventCancelled'));
        return;
      }
      
      if (eventData.status === 'ended') {
        toast.error(t('events.share.eventEnded'));
        return;
      }
    }

    try {
      const inviteLink = `${window.location.origin}/invite/${eventId}`;
      await navigator.clipboard.writeText(inviteLink);
      toast.success(t('events.share.linkCopied'));
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error(t('events.share.errorCopying'));
    }
  };

  return (
    <div className="share-event-popup">
      <div className="share-event-popup-header">
        <h2>{t('events.share.title')}</h2>
        <button className="share-event-popup-close" onClick={onClose} aria-label={t('shared.common.close')}>
          ×
        </button>
      </div>

      <div className="share-event-popup-tabs">
        <button
          className={`share-event-popup-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          {t('events.share.sendToFriends')}
        </button>
        <button
          className={`share-event-popup-tab ${activeTab === 'link' ? 'active' : ''}`}
          onClick={() => setActiveTab('link')}
        >
          {t('events.share.copyLink')}
        </button>
      </div>

      <div className="share-event-popup-content">
        {activeTab === 'friends' && (
          <div className="share-event-friends-tab">
            {loading ? (
              <Loading className="share-event-loading" size="small" />
            ) : friends.length === 0 ? (
              <div className="share-event-empty">
                <p>{t('events.share.noFriends')}</p>
              </div>
            ) : (
              <>
                <div className="share-event-friends-header">
                  <button
                    className="share-event-select-all"
                    onClick={handleSelectAll}
                    type="button"
                  >
                    {selectedFriends.length === friends.filter(f => f.canInvite).length
                      ? t('events.share.deselectAll')
                      : t('events.share.selectAll')}
                  </button>
                  <span className="share-event-selected-count">
                    {selectedFriends.length} / {friends.filter(f => f.canInvite).length} {t('events.share.selected')}
                  </span>
                </div>
                <div className="share-event-friends-list">
                  {friends.map((friend) => {
                    const friendName = friend?.nickname || 
                                     friend?.name || 
                                     `${friend?.first_name || ''} ${friend?.last_name || ''}`.trim() ||
                                     t('events.share.unknownUser');
                    
                    return (
                      <label
                        key={friend.id}
                        className={`share-event-friend-item ${selectedFriends.includes(friend.id) ? 'selected' : ''} ${!friend.canInvite ? 'disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFriends.includes(friend.id)}
                          onChange={() => handleFriendToggle(friend.id)}
                          disabled={!friend.canInvite}
                        />
                        <UserListAvatar
                          user={friend}
                          className="share-event-friend-avatar"
                          variantWrapperClassName="share-event-friend-avatar-placeholder"
                          inactiveTitle={t('events.details.avatarInactiveAria')}
                          bannedTitle={t('events.details.avatarBannedAria')}
                          alt={friendName}
                          fallback={
                            <span className="share-event-friend-avatar-initial" aria-hidden>
                              {friendName.charAt(0).toUpperCase() || '?'}
                            </span>
                          }
                        />
                        <div className="share-event-friend-info">
                          <span className="share-event-friend-name">
                            {friendName}
                          </span>
                          {friend.status && (
                            <span className={`share-event-friend-status share-event-friend-status-${friend.status}`}>
                              {friend.status === 'host' 
                                ? t('events.share.status.host')
                                : friend.status === 'participant'
                                ? t('events.share.status.alreadyJoined')
                                : ''}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="share-event-actions">
                  <button
                    className="share-event-send-button"
                    onClick={handleSendInvitations}
                    disabled={selectedFriends.length === 0 || sending}
                    type="button"
                  >
                    {sending ? t('shared.common.sending') : t('events.share.sendInvitations')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'link' && (
          <div className="share-event-link-tab">
            <p className="share-event-link-description">
              {t('events.share.linkDescription')}
            </p>
            <div className="share-event-link-container">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/invite/${eventId}`}
                className="share-event-link-input"
              />
              <button
                className="share-event-copy-button"
                onClick={handleCopyLink}
                type="button"
              >
                {t('events.share.copy')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareEventPopup;
